import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { BrandMark } from "@/components/shell/brand-logo";
import { CopyableReference } from "@/components/ui/copyable-reference";
import { acceptTerms, fetchCurrentTerms, type CurrentTerms } from "@/lib/api";
import { parseTermsMarkdown, type TermsBlock } from "@/lib/terms-markdown";
import { TERMS_PATH, termsNavigation, termsReturnPath } from "@/lib/terms-gate";
import { useAuthStore } from "@/stores/auth-store";

/**
 * The terms acceptance page (ALL-5722, specs/terms-acceptance-gate S-10 and
 * the UX pass; page spec `terms-acceptance`).
 *
 * Rendered by RequireAuth in place of every route while the auth step is
 * `terms`, and at `/terms` (where admin-portal and enterprise-wallet send a
 * person whose call answered 403 AUTH_TERMS_ACCEPTANCE_REQUIRED). While it is
 * shown, the only calls are GET /v1/terms/current, POST /v1/terms/acceptances
 * and, on Decline, POST /auth/logout.
 *
 * The document is Markdown from the server. It is never injected as HTML:
 * parseTermsMarkdown returns plain blocks rendered as React text nodes.
 */

export const TERMS_COPY = {
  loading: "Loading Alloy's terms",
  heading: "Review and accept Alloy's terms",
  subheading: (version: string, date: string) =>
    `Version ${version}, effective ${date}. You need to accept these terms to use Alloy.`,
  checkbox: (version: string) => `I have read and accept Alloy's terms (version ${version}).`,
  accept: "Accept and continue",
  accepting: "Recording your acceptance…",
  decline: "Decline and sign out",
  footnote: "We record the version you accept, the time, and your IP address and browser for audit.",
  versionChanged: "Alloy's terms changed while you were reading. Review the current version.",
  error: "We couldn't load Alloy's terms. Try again.",
  retry: "Retry",
} as const;

type Status = "loading" | "ready" | "accepting" | "error";

/** "1 October 2026" in UTC; the raw value when it is not a date. */
export function formatEffectiveDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" }).format(date);
}

function TermsDocument({ blocks }: { blocks: TermsBlock[] }) {
  return (
    <>
      {blocks.map((block, index) => {
        const key = `${block.kind}-${index}`;
        if (block.kind === "heading") {
          // The page's own h1 is the heading; document levels start at h2.
          if (block.level === 1) return <h2 key={key} className="mb-2 mt-4 text-base font-semibold text-ink first:mt-0">{block.text}</h2>;
          if (block.level === 2) return <h3 key={key} className="mb-2 mt-4 text-sm font-semibold text-ink first:mt-0">{block.text}</h3>;
          return <h4 key={key} className="mb-1 mt-3 text-sm font-semibold text-ink first:mt-0">{block.text}</h4>;
        }
        if (block.kind === "list") {
          return (
            <ul key={key} className="mb-3 list-disc space-y-1 pl-5">
              {block.items.map((item, itemIndex) => (
                <li key={`${key}-${itemIndex}`}>{item}</li>
              ))}
            </ul>
          );
        }
        return <p key={key} className="mb-3">{block.text}</p>;
      })}
    </>
  );
}

export default function TermsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const step = useAuthStore((s) => s.step);
  const signOut = useAuthStore((s) => s.signOut);
  const goLogin = useAuthStore((s) => s.goLogin);
  const termsAccepted = useAuthStore((s) => s.termsAccepted);

  const [status, setStatus] = useState<Status>("loading");
  const [terms, setTerms] = useState<CurrentTerms | null>(null);
  const [checked, setChecked] = useState(false);
  const [versionChanged, setVersionChanged] = useState(false);
  const [supportReference, setSupportReference] = useState<string | undefined>(undefined);
  const [leaving, setLeaving] = useState(false);
  const loadSeq = useRef(0);
  const mounted = useRef(true);

  const onTermsRoute = location.pathname === TERMS_PATH;

  // After acceptance: on /terms, a full navigation to the validated return_to
  // (it may be another app on this origin); anywhere else the shell re-checks
  // the session and the page the person asked for renders in place.
  const proceed = useCallback(async () => {
    if (onTermsRoute) {
      setLeaving(true);
      termsNavigation.assign(termsReturnPath(location.search));
      return;
    }
    await termsAccepted();
  }, [location.search, onTermsRoute, termsAccepted]);

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    setStatus("loading");
    setChecked(false);
    const result = await fetchCurrentTerms();
    if (!mounted.current || seq !== loadSeq.current) return;
    if (!result.ok) {
      setSupportReference(result.supportReference);
      setStatus("error");
      return;
    }
    setSupportReference(undefined);
    setTerms(result.terms);
    setStatus("ready");
    // Already accepted (for instance in another tab) and reached through /terms
    // while the shell itself is not blocked: continue to where they were going.
    if (result.terms.acceptedByCaller && step !== "terms" && onTermsRoute) void proceed();
  }, [onTermsRoute, proceed, step]);

  useEffect(() => {
    mounted.current = true;
    void load();
    return () => {
      mounted.current = false;
    };
    // Load once per mount; a reload is explicit (Retry or a version change).
  }, []);

  async function accept() {
    if (!terms || !checked || status !== "ready") return;
    setStatus("accepting");
    const result = await acceptTerms(terms.version, terms.documentSha256);
    if (!mounted.current) return;
    if (result.ok) {
      await proceed();
      return;
    }
    if (result.versionChanged) {
      setVersionChanged(true);
      await load();
      return;
    }
    setSupportReference(result.supportReference);
    setStatus("error");
  }

  async function decline() {
    if (status === "accepting" || leaving) return;
    setLeaving(true);
    navigate("/login", { replace: true });
    try {
      await signOut();
    } catch {
      // The logout call failed; the person still leaves the product.
      goLogin();
    }
  }

  const blocks = useMemo(() => (terms ? parseTermsMarkdown(terms.documentMarkdown) : []), [terms]);
  const busy = status === "accepting" || leaving;
  const dataState = status === "ready" && versionChanged ? "version-changed" : status;

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-bg p-4" data-testid="terms-page" data-state={dataState}>
      <div className="auth-card w-full" style={{ maxWidth: 720 }}>
        <div className="auth-card__inner">
          <div className="auth-card__content" style={{ maxWidth: 720, gap: 24, alignItems: "stretch" }}>
            <div className="grid size-14 place-items-center self-center rounded-xl bg-primary text-white">
              <BrandMark size={32} />
            </div>

            <div className="auth-heading self-center" style={{ maxWidth: 640 }}>
              <h1 className="auth-title" style={{ fontSize: 24, lineHeight: "32px" }}>
                {TERMS_COPY.heading}
              </h1>
              {terms && status !== "error" && (
                <p className="auth-subtitle" style={{ fontSize: 15 }}>
                  {TERMS_COPY.subheading(terms.version, formatEffectiveDate(terms.effectiveAt))}
                </p>
              )}
            </div>

            {versionChanged && status !== "error" && (
              <div role="status" className="rounded-lg bg-primary-soft px-3.5 py-3 text-xs leading-relaxed text-primary">
                {TERMS_COPY.versionChanged}
              </div>
            )}

            {status === "loading" && (
              <div className="flex flex-col items-center gap-3 py-8">
                <div className="spinner" aria-label={TERMS_COPY.loading} role="status" />
                <p className="text-xs text-muted">{TERMS_COPY.loading}</p>
              </div>
            )}

            {status === "error" && (
              <div role="alert" className="flex flex-col items-center gap-3 py-4 text-center">
                <p className="text-sm font-semibold text-ink">{TERMS_COPY.error}</p>
                <CopyableReference reference={supportReference} compact />
                <button
                  type="button"
                  onClick={() => {
                    void load();
                  }}
                  className="rounded-md border border-line px-4 py-2 text-xs font-semibold text-ink hover:border-primary"
                >
                  {TERMS_COPY.retry}
                </button>
              </div>
            )}

            {terms && (status === "ready" || status === "accepting") && (
              <>
                <section
                  aria-label={terms.title}
                  tabIndex={0}
                  data-testid="terms-document"
                  className="overflow-y-auto rounded-lg border border-line bg-surface p-4 text-left text-sm leading-relaxed text-text outline-none focus-visible:ring-2 focus-visible:ring-primary-soft"
                  style={{ maxHeight: "60vh" }}
                >
                  <TermsDocument blocks={blocks} />
                </section>

                <label className="flex items-start gap-3 text-left text-sm text-ink">
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={busy}
                    onChange={(event) => setChecked(event.target.checked)}
                    className="mt-0.5 size-4 shrink-0 accent-primary"
                  />
                  <span>{TERMS_COPY.checkbox(terms.version)}</span>
                </label>

                <div className="flex w-full flex-col items-center gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      void accept();
                    }}
                    disabled={!checked || busy}
                    aria-busy={status === "accepting"}
                    className="flex h-11 w-full items-center justify-center rounded-lg bg-primary px-4 text-sm font-semibold text-white outline-none transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-primary-soft disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {status === "accepting" ? TERMS_COPY.accepting : TERMS_COPY.accept}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      void decline();
                    }}
                    disabled={busy}
                    className="text-xs font-medium text-muted outline-none hover:underline focus-visible:underline"
                  >
                    {TERMS_COPY.decline}
                  </button>
                </div>

                <p className="text-center text-xs text-muted">{TERMS_COPY.footnote}</p>
              </>
            )}

            {status === "error" && (
              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={() => {
                    void decline();
                  }}
                  disabled={busy}
                  className="text-xs font-medium text-muted outline-none hover:underline focus-visible:underline"
                >
                  {TERMS_COPY.decline}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
